import api from './api.service';
import type { ApiResponse } from '@/types';
import type { CrmActivity } from './crmActivity.service';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export interface Lead {
  id: string;
  company_id: string;
  lead_number: string;
  customer_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  source?: string;
  industry?: string;
  company_size?: string;
  annual_revenue?: number;
  lead_status: string;
  lead_score: number;
  qualification_level?: number;
  lead_quality_score?: number;
  business_challenge?: string;
  required_standards?: string;
  estimated_project_value?: number;
  expected_timeline_weeks?: number;
  strategic_importance?: string;
  priority: string;
  assigned_to?: string;
  assigned_date?: string;
  next_follow_up_date?: string;
  last_contact_date?: string;
  description?: string;
  notes?: string;
  revenue_service_type_id?: string;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  is_converted?: boolean;
  converted_to_opportunity_id?: string;
  conversion_date?: string;
  customer_id?: string | null;
  status_reason?: string | null;
  status_reason_notes?: string | null;
  status_reason_at?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
  assignedUser?: { id: string; first_name: string; last_name?: string; email?: string };
}

/** Payload for create / update (snake_case sent to API) */
export interface LeadInput {
  customer_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  source?: string;
  industry?: string;
  company_size?: string;
  annual_revenue?: number;
  lead_status?: string;
  lead_score?: number;
  priority?: string;
  assigned_to?: string | null;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  next_follow_up_date?: string | null;
  description?: string;
  notes?: string;
  business_challenge?: string;
  required_standards?: string;
  estimated_project_value?: number;
  expected_timeline_weeks?: number;
  qualification_level?: number;
  strategic_importance?: string;
  revenue_service_type_id?: string | null;
  customer_id?: string | null;
  status_reason?: string | null;
  status_reason_notes?: string | null;
}

export type LeadActivity = CrmActivity;

/** Lead statuses that close the lead and need a reason. */
export const LEAD_CLOSED_STATUSES = ['Lost', 'Unqualified'];

export const LEAD_STATUS_REASONS = [
  'No budget',
  'No requirement',
  'Lost to competitor',
  'Price',
  'Timing / postponed',
  'No response',
  'Not the decision maker',
  'Out of scope',
  'Duplicate lead',
  'Other',
];

export interface LeadStatusReasonInput {
  status_reason?: string | null;
  status_reason_notes?: string | null;
}

export interface LeadDuplicateQuery {
  customer_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  exclude_id?: string;
}

export interface LeadDuplicateMatches {
  leads: Array<{
    id: string;
    lead_number: string;
    customer_name: string;
    contact_person: string | null;
    email: string | null;
    phone: string | null;
    lead_status: string;
    owner_name: string | null;
    matched_on: string[];
  }>;
  customers: Array<{ id: string; customer_code: string | null; customer_name: string; matched_on: string[] }>;
}

class LeadService {
  async getAll(filters?: {
    lead_status?: string;
    assigned_to?: string;
    priority?: string;
    search?: string;
    sales_team_id?: string;
    missing_team?: boolean;
  }): Promise<Lead[]> {
    const params = new URLSearchParams();
    if (filters?.lead_status) params.append('lead_status', filters.lead_status);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sales_team_id) params.append('sales_team_id', filters.sales_team_id);
    if (filters?.missing_team) params.append('missing_team', 'true');
    const q = params.toString();
    const res = await api.get<Lead[]>(`/crm/leads${q ? `?${q}` : ''}`);
    const data = unwrap<Lead[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async getById(id: string): Promise<Lead> {
    const res = await api.get<Lead>(`/crm/leads/${id}`);
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Lead not found');
    return data;
  }

  async create(body: LeadInput): Promise<Lead> {
    const res = await api.post<Lead>('/crm/leads', body);
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Failed to create lead');
    return data;
  }

  async update(id: string, body: Partial<LeadInput>): Promise<Lead> {
    const res = await api.put<Lead>(`/crm/leads/${id}`, body);
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Failed to update lead');
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/crm/leads/${id}`);
  }

  async updateStatus(id: string, status: string, reason?: LeadStatusReasonInput): Promise<Lead> {
    const res = await api.post<Lead>(`/crm/leads/${id}/status`, { status, ...reason });
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Failed to update status');
    return data;
  }

  async findDuplicates(q: LeadDuplicateQuery): Promise<LeadDuplicateMatches> {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(q)) if (v && String(v).trim()) params[k] = String(v).trim();
    const res = await api.get('/crm/leads/duplicates', { params });
    return unwrap<LeadDuplicateMatches>(res) ?? { leads: [], customers: [] };
  }

  async assign(id: string, user_id: string): Promise<Lead> {
    const res = await api.post<Lead>(`/crm/leads/${id}/assign`, { user_id });
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Failed to assign lead');
    return data;
  }

  async convertToOpportunity(id: string, opportunity_id: string): Promise<Lead> {
    const res = await api.post<Lead>(`/crm/leads/${id}/convert`, { opportunity_id });
    const data = unwrap<Lead>(res);
    if (!data) throw new Error('Failed to convert lead');
    return data;
  }

  async getActivities(leadId: string): Promise<LeadActivity[]> {
    const res = await api.get<LeadActivity[]>(`/crm/leads/${leadId}/activities`);
    const data = unwrap<LeadActivity[]>(res);
    return Array.isArray(data) ? data : [];
  }
}

const leadService = new LeadService();
export default leadService;
